/* GASSISA
=========================================================================================
* Descripción: Modal para buscar artículos para etiquetado
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 01/07/2026 |                                    | Creación
*      2       | 01/07/2026 |                                    | Navegación por flechas mejorada
*      3       | 13/08/2026 | Lic. José Luis Aragón Cervantes  | Ajuste de alto y foco del listado
* ---------------------------------------------------------------------------------------
*/

import TextField from "../ui/TextField";
import { useEffect, useState, useRef } from "react"
import { Modal, Box, Typography } from "@mui/material";
import { useRequest } from "../../context/System/RequestContext"
import { useLayout } from "../../context/System/LayoutContext"
import { apiFiltroExistenciasAlmacenExhibidor } from "../../settings/ApiConfig"
import SizeSoftXTableExistencias from "../leyout/SizeSoftXTableExistencias"

const modalStyle = {
    position: 'fixed',
    top: '6%',
    right: '9%',
    width: '81vw',
    height: '90vh',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 0,
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '9px',
    overflow: 'hidden'
};

const base64ToReaderResult = (base64, mimeType = "image/png") => {
    if (!base64) return null;
    return base64.startsWith('data:') ? base64 : `data:${mimeType};base64,${base64}`;
};

export default function ModalBuscarArticulosEtiquetado({
    openModal,
    handleCloseModal,
    almacenexhibidor,
    onArticuloSelect
}) {
    const { PostRequest } = useRequest()
    const { handleOpenAlert } = useLayout()

    const filtroRef = useRef(null)
    const tablaAreaRef = useRef(null)

    const [filtro, setFiltro] = useState("")
    const [lista, setLista] = useState([])
    const [articuloSel, setArticuloSel] = useState({})
    const [statusWaiting, setStatusWaiting] = useState(false)
    const [medida, setMedida] = useState(0)

    useEffect(() => {
        if (openModal) {
            setTimeout(() => {
                filtroRef.current?.focus()
                filtroRef.current?.select()
            }, 0)
        }
    }, [openModal])

    useEffect(() => {
        if (!openModal) return
        const handleKeyDown = (e) => {
            if (e.altKey && e.key.trim().toUpperCase() === 'F1') {
                e.preventDefault()
                e.stopPropagation()
                filtroRef.current?.focus()
                filtroRef.current?.select()
            }
        }
        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [openModal])

    useEffect(() => {
        if (!openModal) return

        const handleCapture = (e) => {
            if (e.key === 'ArrowUp') {
                const tableContainer = tablaAreaRef.current?.querySelector('.MuiTableContainer-root')
                if (tableContainer?.contains(document.activeElement)) {
                    if (lista.length > 0 && articuloSel === lista[0]) {
                        e.preventDefault()
                        e.stopPropagation()
                        filtroRef.current?.focus()
                        filtroRef.current?.select()
                    }
                }
            }
            if (e.key === 'Escape') {
                handleCloseModal()
            }
        }

        document.addEventListener('keydown', handleCapture, { capture: true })
        return () => document.removeEventListener('keydown', handleCapture, { capture: true })
    }, [openModal, lista, articuloSel])

    const handleChangeFilter = (e) => {
        setFiltro(e.target.value)
    }

    const handleBuscar = async () => {
        if (!filtro || !almacenexhibidor) return

        setStatusWaiting(true)

        const response = await PostRequest({
            url: apiFiltroExistenciasAlmacenExhibidor,
            body: { filtro: filtro.trim(), almacenexhibidor: almacenexhibidor.trim() },
            loader: "Buscando artículos..."
        })

        setStatusWaiting(false)

        if (response.HasError) {
            handleOpenAlert(response.message || "Error al buscar artículos")
            setLista([])
            return
        }

        const dataConFotos = (response?.result || []).map(articulo => {
            const rawFoto = articulo.foto || articulo.imagen || articulo.Foto || articulo.Imagen || articulo.FOTO || articulo.IMAGEN
            return {
                ...articulo,
                foto: rawFoto ? base64ToReaderResult(rawFoto) : null
            }
        })

        setLista(dataConFotos)

        if (dataConFotos.length > 0) {
            setArticuloSel(dataConFotos[0])
            setTimeout(() => {
                const tableContainer = tablaAreaRef.current?.querySelector('.MuiTableContainer-root')
                tableContainer?.focus()
            }, 150)
        }
    }

    const handleOnKeyDownFilter = async (e) => {
        if (e.key === 'Enter') {
            e.preventDefault()
            await handleBuscar()
        }

        if (e.key === 'ArrowDown') {
            e.preventDefault()
            if (lista.length > 0) {
                setArticuloSel(lista[0])
                setTimeout(() => {
                    const tableContainer = tablaAreaRef.current?.querySelector('.MuiTableContainer-root')
                    tableContainer?.focus()
                }, 10)
            }
        }
    }

    const handleDobleClick = async (row) => {
        if (!row) return
        await onArticuloSelect(row)
        handleCloseModal()
    }

    const handleEnterKey = async (row) => {
        if (!row) return
        await onArticuloSelect(row)
        handleCloseModal()
    }

    return (
        <Modal open={openModal} onClose={handleCloseModal}>
            <Box sx={modalStyle}>
                <Box sx={{ backgroundColor: '#23165d', padding: '10px', borderRadius: '4px 4px 0 0' }}>
                    <Typography variant="h6" sx={{ color: 'white', margin: 0, fontWeight: 'bold' }}>
                        Buscador de artículos para etiquetado
                    </Typography>
                </Box>
                <div className="filtrobuscadorarticulos" style={{ padding: '10px', backgroundColor: '#f5f5f5' }}>
                    <TextField
                        inputRef={filtroRef}
                        label="Filtro (Enter para buscar):"
                        value={filtro}
                        onChange={handleChangeFilter}
                        onKeyDown={handleOnKeyDownFilter}
                        autoComplete="off"
                        size="small"
                        sx={{ width: '300px' }}
                    />
                </div>
                <div ref={tablaAreaRef} className="tablaarticulosfiltrados" style={{ height: 'calc(90vh - 72px)', maxHeight: 'none', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: '4px 8px 10px' }}>
                    <SizeSoftXTableExistencias
                        columns={[
                            { title: "Clave artículo", field: "clavearticulo", hideSort: false },
                            { title: "Descripción", field: "descripcion", hideSort: false },
                        ]}
                        data={lista}
                        id="id"
                        row={articuloSel}
                        setRow={setArticuloSel}
                        fontSize="small"
                        rowsPerPageIntro={8}
                        activaProgress={statusWaiting}
                        setClave={() => {}}
                        setMedida={setMedida}
                        onRowDoubleClick={handleDobleClick}
                        onEnterKey={handleEnterKey}
                        disablePagination={true}
                        fillContainer={true}
                    />
                </div>
            </Box>
        </Modal>
    )
}
